/* eslint-disable */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

/*import url and gateway methods */
import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

//import images
import { assetImages } from "constants";

//import current date
import { getCurrentDateInString } from "helper/getcurrentdatestring";

//import membership options from json
import GroupMembershipStatusOption from "data/Prod/MembershipStatus.json"; // Assuming this file exists or needs to be created/adapted

/**
 * GroupPartnerDetailsPopup Component
 * Displays and manages partner/member details in an offcanvas popup
 * Allows updating partner role and membership status
 */
const GroupPartnerDetailsPopup = ({
  roleList,
  partnerId,
  setPartnerId,
  afterPopupClose,
  groupId, // Changed from eventId to groupId
}) => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation(); //for translation

  // ========== STATE MANAGEMENT ==========

  /** Group member unique identifier */
  const [groupMemberId, setGroupMemeberId] = useState(null); // Changed from eventMemberId

  /** Partner/member profile information */
  const [memberImage, setmemberImage] = useState("");
  const [memberName, setmemberName] = useState("");
  const [memberEmail, setmemberEmail] = useState("");
  const [memberRole, setmemberRole] = useState("");

  /** Role selection state management */
  const [roleValue, setRoleValue] = useState(null);
  const [roleData, setRoleData] = useState(null);

  /** Membership status state management */
  const [memberStatusValue, setmemberStatusValue] = useState(null);
  const [memberStatusData, setmemberStatusData] = useState(null);
  const [groupMemberStatus, setGroupMemberStatus] = useState("0"); // Changed from eventMemberStatus

  /** Loading state for save operations */
  const [isSaving, setIsSaving] = useState(false);
  /** Loading state for fetching member details */
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // ========== API FUNCTIONS ==========

  /**
   * Fetches detailed information about the group member/partner
   * Populates form fields with existing data for editing
   */
  const getMemberDetails = async () => {
    if (!partnerId) return; // Prevent API call if partnerId is null

    try {
      setIsLoadingDetails(true); // Set loading state to true

      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_GROUP_MEMBER_DETAILS + // Changed API endpoint
        `/${partnerId}`;

      const response = await getData(requestUrl);
      console.log("getMemberDetails response --------> ", response);

      setIsLoadingDetails(false); // Set loading state to false

      if (response.status) {
        setGroupMemeberId(response.data.groupsponsorid); // Changed from eventsponsorid
        setmemberImage(response.data.logo);
        setmemberName(response.data.companyname);
        setmemberEmail(response.data.companyemail);
        // setmemberRole(response.data.rolestring);
        roleSelectionHandler(response.data.roledata);
        GroupMembershipStatusOption.map((membership) => {
          if (response.data.moderatorstatus == membership.value) {
            memberShipStatusSelectionHandler(membership);
          }
        });
        setGroupMemberStatus(response.data.memberstatus); // Changed from eventMemberStatus
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Updates partner membership information including role and status
   * Handles API call and popup closure on success
   */
  const updateMemberHandler = async () => {
    try {
      setIsSaving(true);

      let memberData = {
        moderatorstatus: memberStatusData,
        joiningdate: memberStatusData == "1" ? getCurrentDateInString() : "",
      };

      let requestUrl =
        url.API_BASE_URL +
        url.API_UPDATE_GROUP_MEMBER + // Changed API endpoint
        `/${partnerId}?group=${groupId}&groupsponsorid=${groupMemberId}&groupmemberstatus=${groupMemberStatus}`; // Changed parameters

      const response = await putData(requestUrl, memberData);

      console.log(response);

      setIsSaving(false);

      if (response.status) {
        //hide member modal
        // The offcanvas should be controlled by data-bs-dismiss attribute
        // let offCanvasPopup = document.querySelector("#offcanvasPartnerDetails");
        // let offCanvas = bootstrap.Offcanvas.getInstance(offCanvasPopup);
        // offCanvas.hide();

        closePopupHandler();
        afterPopupClose();
      }
    } catch (error) {
      console.log(error.message);
      setIsSaving(false);
    }
  };

  // ========== EVENT HANDLERS ==========

  /**
   * Handles role selection from dropdown
   * Updates both display value and data for API submission
   */
  const roleSelectionHandler = (val) => {
    setRoleValue(val);
    setRoleData(val?.value);
  };

  /**
   * Handles membership status selection
   * Updates both display value and data for API submission
   */
  const memberShipStatusSelectionHandler = (val) => {
    setmemberStatusValue(val);
    setmemberStatusData(val?.value);
  };

  /**
   * Resets all form states when popup is closed
   * Ensures clean state for next usage
   */
  const closePopupHandler = () => {
    setPartnerId(null);
    setGroupMemeberId(null); // Changed from eventMemberId
    setmemberImage("");
    setmemberName("");
    setmemberEmail("");
    setmemberRole("");
    setRoleValue(null);
    setRoleData(null);
    setmemberStatusValue(null);
    setmemberStatusData(null);
  };

  // ========== CUSTOM STYLES ==========

  /** Custom styles for react-select components */
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: "#ced4da",
      "&:hover": {
        borderColor: "#ced4da",
      },
      minHeight: "40px",
      boxShadow: "none",
      padding: "3px",
      borderRadius: "6px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#6c757d",
    }),
    input: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.875rem",
    }),
  };

  // ========== LIFECYCLE EFFECTS ==========

  /**
   * Fetches member details when partnerId is provided
   * Triggers on component mount or partnerId change
   */
  useEffect(() => {
    if (partnerId) {
      getMemberDetails();
    }
  }, [partnerId]);

  return (
    <div
      className="offcanvas md offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="offcanvasGroupPartnerDetails" // Changed ID
      aria-labelledby="offcanvasMemberDetailsLabel"
    >
      {/* ========== POPUP HEADER ========== */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              group
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasMemberDetailsLabel"
            >
              {t("Partner Details")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {t("Manage partner roles and membership status")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={closePopupHandler}
        ></button>
      </div>

      {/* ========== POPUP BODY ========== */}
      <div className="offcanvas-body d-flex flex-column p-4">
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
          {/* Loading State */}
          {isLoadingDetails && (
            <div className="loading-state text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">{t("Loading partner details...")}</p>
            </div>
          )}

          {/* No Partner Selected State */}
          {!isLoadingDetails && !partnerId && (
            <div className="no-response-state text-center py-5">
              <div
                className="icon-wrapper p-4 bg-warning-subtle rounded-circle mx-auto mb-3"
                style={{ width: "80px", height: "80px" }}
              >
                <span className="material-symbols-outlined text-warning fs-1">
                  person_search
                </span>
              </div>
              <h5 className="fw-semibold text-dark mb-2">
                {t("No Partner Selected")}
              </h5>
              <p className="text-muted fs-sm mb-0">
                {t(
                  "Please select a partner from the group list to view and manage their details."
                )}
              </p>
            </div>
          )}

          {/* Partner Details Content */}
          {!isLoadingDetails && partnerId && (
            <>
              {/* ========== PARTNER PROFILE SECTION ========== */}
              <div className="profile-section mb-4 p-3 bg-light rounded-10">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="avatar rounded-circle overflow-hidden border border-gray-300"
                    style={{ width: "50px", height: "50px" }}
                  >
                    <img
                      src={
                        memberImage == ""
                          ? assetImages.defaultUser
                          : url.SERVER_URL + memberImage
                      }
                      alt="Partner Avatar"
                      className="w-100 h-100 object-fit-cover object-center"
                    />
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="fw-semibold mb-1 text-dark">{memberName}</h5>
                    <p className="text-muted mb-0 fs-sm">
                      {t("Group Partner")} {/* Changed text */}
                    </p>
                  </div>
                </div>
              </div>

              {/* ========== CURRENT INFORMATION TABLE ========== */}
              <div className="current-info-section mb-4 p-3 bg-light rounded-10 border border-gray-200 shadow-sm">
                <h6 className="fw-semibold mb-3 text-secondary d-flex align-items-center gap-2">
                  <span className="material-symbols-outlined fs-5 text-primary">
                    info
                  </span>
                  {t("Current Information")}
                </h6>
                <div className="table-responsive">
                  <table className="table table-borderless fs-sm">
                    <tbody>
                      {/* <tr className="border-bottom border-gray-200">
                        <td className="bg-transparent py-2 text-muted d-flex align-items-center gap-2">
                          <span className="material-symbols-outlined fs-6 text-primary">
                            work
                          </span>
                          {t("Current Role")}
                        </td>
                        <td className="bg-transparent py-2 fw-semibold text-dark">
                          {memberRole}
                        </td>
                      </tr> */}
                      <tr className="border-bottom border-gray-200">
                        <td className="bg-transparent py-2 text-muted d-flex align-items-center gap-2">
                          <span className="material-symbols-outlined fs-6 text-primary">
                            mail
                          </span>
                          {t("Email")}
                        </td>
                        <td className="bg-transparent py-2 fw-semibold text-dark">
                          {memberEmail}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ========== EDIT FORM SECTION ========== */}
          {!isLoadingDetails && partnerId && (
            <div className="edit-form-section flex-grow-1">
              {/* <h6 className="fw-semibold mb-3 text-secondary border-top border-gray-300 pt-3 d-flex align-items-center gap-2">
                <span className="material-symbols-outlined fs-5 text-primary">
                  edit
                </span>
                {t("Update Partner Details")}
              </h6> */}

              <form onSubmit={(e) => e.preventDefault()}>
                {/* Role Selection Field */}
                {/* <div className="form-group mb-4">
                  <label className="d-block fs-sm fw-semibold mb-2 d-flex align-items-center gap-2">
                    <span className="material-symbols-outlined fs-5 text-primary">
                      assignment_ind
                    </span>
                    {t("Assign Role")} <span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    placeholder={t(
                      "Select a role for this partner (e.g., Sponsor, Organizer)"
                    )}
                    isClearable
                    options={roleList}
                    value={roleValue}
                    onChange={roleSelectionHandler}
                  />
                </div> */}

                {/* Membership Status Field */}
                <div className="form-group mb-4">
                  <label className="d-block fs-sm fw-semibold mb-2 d-flex align-items-center gap-2">
                    <span className="material-symbols-outlined fs-5 text-primary">
                      how_to_reg
                    </span>
                    {t("Membership Status")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={customSelectStyles}
                    placeholder={t(
                      "Choose membership status (e.g., Active, Pending, Inactive)"
                    )}
                    isClearable
                    options={GroupMembershipStatusOption}
                    value={memberStatusValue}
                    onChange={memberShipStatusSelectionHandler}
                  />
                </div>

                {/* Action Buttons */}
                <div className="action-buttons-section mt-4 pt-3 border-top border-gray-300">
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-primary px-4 d-flex align-items-center gap-2"
                      onClick={updateMemberHandler}
                      disabled={isSaving || !roleData || !memberStatusData}
                    >
                      {isSaving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {t("Updating...")}
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined fs-5">
                            save
                          </span>
                          {t("Update Partner")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupPartnerDetailsPopup;
